import SearchIcon from '@mui/icons-material/Search';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  Box,
  Card,
  CardContent,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';

import AvailableClassesDialog from '../components/Class/AvailableClassesDialog';
import AssessmentMiniDrawer from '../components/AssessmentMiniDrawer';
import MiniDrawer from '../components/MiniDrawer';
import AppBarSpacer from '../components/common/AppBarSpacer';
import ColorChip from '../components/common/ColorChip';
import PageHeader from '../components/common/PageHeader';
import { PendingEnrollmentRow } from '../types/PendingEnrollment.type';
import { getPendingEnrollments } from '../utils/pendingEnrollmentApi';
import {
  levelAlternativeChipColors,
  levelChipColors,
  serviceTypeColors,
} from '../utils/colorHandler';
import { notifyFailure } from '../utils/toast';

const PendingEnrollment = () => {
  const isAdmin =
    JSON.parse(localStorage.getItem('aquatics_hub_2025_user') || 'null')
      ?.role === 'ADMIN';
  const [open, setOpen] = useState(true);
  const [rows, setRows] = useState<PendingEnrollmentRow[]>([]);
  const [row, setRow] = useState<PendingEnrollmentRow | null>(null);
  const [serviceType, setServiceType] = useState('');
  const [packageName, setPackageName] = useState('');
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchRows = async () => {
    try {
      setLoading(true);
      const response = await getPendingEnrollments({
        serviceType,
        package: packageName,
        search,
        registrationDateFrom: fromDate?.format('YYYY-MM-DD') || '',
        registrationDateTo: toDate?.format('YYYY-MM-DD') || '',
        page: page + 1,
        limit,
      });
      setRows(response.data);
      setTotal(response.metadata.totalItems);
    } catch {
      notifyFailure();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchRows();
  }, [serviceType, packageName, fromDate, toDate, page, limit]);

  const removeAddedRow = (registrationId: number) => {
    setRows((current) =>
      current.filter((item) => item.registrationId !== registrationId),
    );
    setTotal((current) => Math.max(current - 1, 0));
  };

  return (
    <div className="flex h-screen">
      {isAdmin ? (
        <MiniDrawer open={open} setOpen={setOpen} />
      ) : (
        <AssessmentMiniDrawer open={open} setOpen={setOpen} />
      )}
      <main className="flex-1 overflow-auto app-page-bg">
        <AppBarSpacer />
        <div className="h-full px-6 py-4">
          <PageHeader
            title="Pending Enrollment"
            subtitle="Course registrations that are not linked to an active class."
          />
          <Card>
            <CardContent>
              <Box display="flex" gap={1.5} flexWrap="wrap" mb={2}>
                <TextField
                  size="small"
                  label="Student ID / name"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPage(0);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') void fetchRows();
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Service type</InputLabel>
                  <Select
                    label="Service type"
                    value={serviceType}
                    onChange={(event) => {
                      setServiceType(event.target.value);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="SOF">SOF</MenuItem>
                    <MenuItem value="FLYING FISH">Flying Fish</MenuItem>
                    <MenuItem value="ADULT COURSES">Adult Courses</MenuItem>
                    <MenuItem value="PRIVATE LESSON">Private Lesson</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Package"
                  value={packageName}
                  onChange={(event) => {
                    setPackageName(event.target.value);
                    setPage(0);
                  }}
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="From date"
                    format="DD/MM/YYYY"
                    value={fromDate}
                    maxDate={toDate || undefined}
                    onChange={(newDate) => {
                      setFromDate(newDate);
                      setPage(0);
                    }}
                    slotProps={{
                      textField: { size: 'small' },
                      field: {
                        clearable: true,
                        onClear: () => {
                          setFromDate(null);
                          setPage(0);
                        },
                      },
                    }}
                  />
                  <DatePicker
                    label="To date"
                    format="DD/MM/YYYY"
                    value={toDate}
                    minDate={fromDate || undefined}
                    onChange={(newDate) => {
                      setToDate(newDate);
                      setPage(0);
                    }}
                    slotProps={{
                      textField: { size: 'small' },
                      field: {
                        clearable: true,
                        onClear: () => {
                          setToDate(null);
                          setPage(0);
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 1000 }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#4da7db' }}>
                      {[
                        'Student ID',
                        'Full name',
                        'Age',
                        'Program',
                        'Package',
                        'Level',
                        'Remaining',
                        'Registration date',
                        'End date',
                        'Action',
                      ].map((label) => (
                        <TableCell
                          key={label}
                          sx={{ color: '#fff', fontWeight: 700 }}
                        >
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          No pending enrollment.
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((item) => (
                        <TableRow key={item.registrationId} hover>
                          <TableCell>{item.studentId}</TableCell>
                          <TableCell>{item.fullName}</TableCell>
                          <TableCell>{item.age}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <ColorChip
                              label={item.program}
                              palette={serviceTypeColors}
                            />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <ColorChip label={item.packageName} />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <ColorChip
                              label={item.level}
                              palette={{
                                ...levelChipColors,
                                ...levelAlternativeChipColors,
                              }}
                              emptyLabel="—"
                            />
                          </TableCell>
                          <TableCell>
                            {item.remainingSessions === null
                              ? 'Unlimited'
                              : item.remainingSessions}
                          </TableCell>
                          <TableCell>{item.registrationDate}</TableCell>
                          <TableCell>{item.endDate}</TableCell>
                          <TableCell>
                            <button
                              className="rounded bg-primary px-3 py-1 text-white"
                              onClick={() => setRow(item)}
                            >
                              Choose class
                            </button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="caption" color="text.secondary">
                  Only active course packages with no active ClassStudent are
                  shown.
                </Typography>
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  rowsPerPage={limit}
                  onPageChange={(_, next) => setPage(next)}
                  onRowsPerPageChange={(event) => {
                    setLimit(Number(event.target.value));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </Box>
            </CardContent>
          </Card>
        </div>
      </main>
      <AvailableClassesDialog
        open={Boolean(row)}
        row={row}
        onClose={() => setRow(null)}
        onAdded={removeAddedRow}
      />
    </div>
  );
};

export default PendingEnrollment;
